/**
 * Integrations API client.
 *
 * Note what is missing: there is no call that returns a credential, because no route does. The
 * connection view shows which provider account is linked and what state it is in, and nothing more
 * (L3-29 §35, §43).
 */

import { request } from '../../api/client';

export type ConnectionStatus =
  'pending' | 'active' | 'authorization_required' | 'error' | 'revoked' | 'disabled';

export interface Connection {
  id: string;
  provider: string;
  /** Display only — which Google account this is, for somebody who has three. */
  account: string | null;
  status: ConnectionStatus;
  needs_reconnection: boolean;
  calendar_id: string | null;
  scopes: string[];
  last_error_code: string | null;
  connected_at: string;
}

export interface CalendarRef {
  id: string;
  name: string;
  writable: boolean;
}

export interface SyncRecord {
  liturgy_id: string;
  external_event_id: string | null;
  status: string;
  last_error_code: string | null;
  last_synced_at: string | null;
}

export function fetchIntegrations(): Promise<{ enabled: boolean; data: Connection | null }> {
  return request('/integrations');
}

export function beginAuthorization(
  continuationPath: string,
): Promise<{ authorization_url: string }> {
  return request('/integrations/google-calendar/authorize', {
    method: 'POST',
    body: JSON.stringify({ continuation_path: continuationPath }),
  });
}

export function fetchCalendars(connectionId: string): Promise<{ data: CalendarRef[] }> {
  return request(`/integrations/${connectionId}/calendars`);
}

export function chooseCalendar(connectionId: string, calendarId: string): Promise<{ id: string }> {
  return request(`/integrations/${connectionId}/calendar`, {
    method: 'POST',
    body: JSON.stringify({ calendar_id: calendarId }),
  });
}

export function fetchSyncState(connectionId: string): Promise<{ data: SyncRecord[] }> {
  return request(`/integrations/${connectionId}/sync`);
}

export function syncNow(connectionId: string, liturgyId: string): Promise<{ state: string }> {
  return request(`/integrations/${connectionId}/sync/${liturgyId}`, { method: 'POST' });
}

/**
 * Disconnect.
 *
 * `events` is required at the call site rather than defaulted, so no caller can delete somebody's
 * calendar entries by omitting an argument.
 */
export function disconnect(
  connectionId: string,
  events: 'keep' | 'remove',
): Promise<{ id: string; status: string }> {
  return request(`/integrations/${connectionId}/disconnect`, {
    method: 'POST',
    body: JSON.stringify({ events }),
  });
}

/** What each safe error category means, in words somebody can act on (L3-29 §43). */
export const ERROR_COPY: Record<string, string> = {
  authorization_required: 'Access was withdrawn at the provider. Reconnect to continue syncing.',
  rate_limited: 'The provider is asking us to slow down. Syncing will resume on its own.',
  temporarily_unavailable: 'The provider could not be reached. Syncing will be retried.',
  resource_not_found: 'The calendar entry was removed at the provider. A new one will be created.',
  permission_denied: 'This account is not permitted to write to that calendar.',
  configuration_error: 'Something is wrong with our setup here, not with your account.',
};
